import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Calendar, User, Tag, ExternalLink, Copy, CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const PaperDetailPage = () => {
  const { id } = useParams();
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/papers/${id}`)
      .then(res => { if (!res.ok) throw new Error(); return res.json(); })
      .then(setPaper)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const handleCopyCitation = () => {
    if (!paper) return;
    const year = paper.submittedAt ? new Date(paper.submittedAt).getFullYear() : new Date().getFullYear();
    const citation = `${paper.authorName} (${year}). ${paper.title}. ${paper.journalTitle}.${paper.doi ? ' DOI: ' + paper.doi : ''}`;
    navigator.clipboard.writeText(citation).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) return (
    <div className="min-h-96 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (notFound) return (
    <div className="max-w-7xl mx-auto px-4 py-20 text-center">
      <AlertCircle size={40} className="text-[#9CA3AF] mx-auto mb-4" />
      <h2 className="font-serif text-2xl text-[#1F1F1F] mb-2">Paper Not Found</h2>
      <p className="text-[#6B7280] text-sm mb-4">This paper may not be published yet or the link is invalid.</p>
      <Link to="/journals" className="text-[#1A73E8] text-sm hover:underline">Browse Journals</Link>
    </div>
  );

  const keywords = (() => {
    try { return JSON.parse(paper.keywords || '[]'); }
    catch { return paper.keywords ? paper.keywords.split(',').map(k => k.trim()) : []; }
  })();

  const year = paper.submittedAt ? new Date(paper.submittedAt).getFullYear() : new Date().getFullYear();

  return (
    <div className="bg-white min-h-screen">
      <div className="bg-[#F5F7FA] border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-[#6B7280]">
            <Link to="/">Home</Link><span>/</span>
            <Link to="/journals">Journals</Link><span>/</span>
            <span className="text-[#1F1F1F] truncate max-w-xs">{paper.title}</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">
        <Link to="/journals" className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#1A73E8] mb-6">
          <ArrowLeft size={14} /> Back to Journals
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">

            {/* TITLE */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 border border-green-200 font-medium">Published</span>
                <span className="text-xs text-[#9CA3AF]">{paper.journalTitle}</span>
              </div>
              <h1 className="font-serif text-3xl font-semibold text-[#1F1F1F] mb-4">{paper.title}</h1>
              <div className="flex flex-wrap gap-4 text-sm text-[#6B7280]">
                <span className="flex items-center gap-1.5"><User size={14} /> {paper.authorName}</span>
                {paper.affiliation && <span className="flex items-center gap-1.5"><BookOpen size={14} /> {paper.affiliation}</span>}
                <span className="flex items-center gap-1.5"><Calendar size={14} /> {year}</span>
              </div>
            </div>

            {/* ABSTRACT */}
            <div className="border-t pt-6">
              <h2 className="text-lg font-semibold mb-3">Abstract</h2>
              <p className="text-sm">{paper.abstract}</p>
            </div>

            {/* KEYWORDS */}
            {keywords.length > 0 && (
              <div className="border-t pt-6">
                <h2 className="text-lg font-semibold mb-3">Keywords</h2>
                <div className="flex flex-wrap gap-2">
                  {keywords.map((kw, i) => (
                    <span key={i} className="px-3 py-1 text-xs bg-blue-50 text-[#1A73E8] border">{kw}</span>
                  ))}
                </div>
              </div>
            )}

            {/* FULL TEXT */}
            <div className="border-t pt-6">
              <h2 className="text-lg font-semibold mb-3">Full Text</h2>

              {paper.zenodoUrl ? (
                <div className="text-center p-6 border">
                  <a
                    href={paper.zenodoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-[#1A73E8] text-white inline-flex items-center gap-2"
                  >
                    <ExternalLink size={16} /> View on Zenodo
                  </a>
                </div>
              ) : paper.fileUrl ? (
                <div className="space-y-3">
                  <iframe
                    src={paper.fileUrl}
                    title={paper.title}
                    className="w-full border"
                    style={{ height: '600px' }}
                  />
                  <a href={paper.fileUrl} target="_blank" rel="noopener noreferrer" className="text-[#1A73E8] text-sm">
                    Open PDF
                  </a>
                </div>
              ) : (
                <div className="text-sm text-[#9CA3AF] text-center p-6 border">
                  Full text not available
                </div>
              )}
            </div>

            {/* CITATION */}
            <div className="border-t pt-6">
              <h2 className="text-lg font-semibold mb-3">How to Cite</h2>
              <div className="p-4 border relative">
                <p className="text-sm">
                  {paper.authorName} ({year}). <em>{paper.title}</em>. <em>{paper.journalTitle}</em>.
                </p>
                <button onClick={handleCopyCitation} className="absolute top-3 right-3">
                  {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>

          </div>

          {/* SIDEBAR */}
          <div className="space-y-5">
            {paper.doi && (
              <div className="border p-5">
                <h3 className="text-xs mb-3">DOI</h3>
                <a href={paper.zenodoUrl} target="_blank" rel="noopener noreferrer">
                  {paper.doi}
                </a>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default PaperDetailPage;
